import classNames from 'classnames';
import React, { EventHandler, FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { NoRecordTable } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';

const CardList = styled(FlexContainer)`
    padding: 16px 0 32px;
    &.detail-cards {
        padding: 16px 8px 0;
        & > .card:last-of-type {
            margin-bottom: 0;
            border-bottom: none;
        }
    }
`;
const Card = styled(FlexContainer)`
    ${({ theme: { colors, typography } }) => `
    .contract {
        &.borderGreen {
            border-left: 2px solid ${colors.alert.green};
        }
        &.borderRed {
            border-left: 2px solid ${colors.alert.red};
        }
        padding-left: 7px;
        span {
            font-size: 10px;
            font-weight: ${typography.weight.normal};
            line-height: calc(${typography.lineHeight[2]} - 1px);
            color: ${colors.alert.yellow};
        }
    }
    .greenText {
        color: ${colors.alert.green};
    }
    .yelloText {
        color: ${colors.alert.yellow};
    }
    .redText {
        color: ${colors.alert.red};
    }
    .greyText {
        color: ${colors.text.tertiary};
    }
    .action-button {
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        font-weight: ${typography.weight.medium};
        color: ${colors.text.tertiary};
        border: 0.6px solid ${colors.text.tertiary};
        border-radius: 3px;
        padding: 2px 4px;
    }
    border-bottom: 1px solid ${colors.border.light};
    margin-bottom: 16px;
    &:last-of-type {
        margin-bottom: 32px;
        border-bottom: none;
    }
`}
`;
const CardItem = styled.div`
    margin-bottom: 16px;
    width: 50%;
    &.left {
        text-align: left;
    }
    &.right {
        text-align: right;
    }
    &.full-width {
        width: 100% !important;
    }
`;
const Label = styled.div`
    ${({ theme: { colors, typography } }) => `
    font-size: ${typography.size.mobile.paragraph.p2};
    line-height: calc(${typography.lineHeight[2]} - 1px);
    font-weight: ${typography.weight.normal};
    color: ${colors.text.tertiary};
    margin-bottom: 8px;
    width: 100%;
`}
`;
const Value = styled.div`
    ${({ theme: { typography } }) => `
    font-size: ${typography.size.mobile.paragraph.p1};
    line-height: calc(${typography.lineHeight[3]} - 0.5px);
    font-weight: ${typography.weight.normal};
    width: 100%;
    `}
`;

interface ExpandableConfig {
    columnTitle?: ReactNode;
    // eslint-disable-next-line no-unused-vars
    expandedRender: (record: any, expanded: boolean) => ReactNode;
    // eslint-disable-next-line no-unused-vars
    expandColumn: (record: any, expanded: boolean, onClick?: EventHandler<any>) => ReactNode;
    // eslint-disable-next-line no-unused-vars
    onExpand: (record: any, expanded: boolean) => void;
}
interface CardProps {
    className?: string;
    columns: any;
    data: Record<string, any>[];
    expandable?: ExpandableConfig;
}

const StyledCards: FC<CardProps> = ({ columns, data, expandable, className }) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const [expandedCards, setExpandedCards] = useState({} as { [key: string]: boolean });

    const handleExpand = (index: string, record: any) => {
        setExpandedCards((prevState) => ({
            ...prevState,
            [index]: !prevState[index],
        }));
        expandable?.onExpand(record, !expandedCards[index]);
    };
    return data.length === 0 ? (
        <FlexContainer
            styles={{
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '210px',
                maxHeight: '210px',
                width: '100%',
            }}
            className="no-record"
        >
            <NoRecordTable fill={colors.background.primary} stroke={colors.border.dark} />
            <p>{t('no_record')}</p>
        </FlexContainer>
    ) : (
        <CardList
            className={classNames('cards-container', className)}
            styles={{
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
            }}
        >
            {data.map((row: any, index: any) => {
                return (
                    <Card
                        key={index}
                        className="card"
                        styles={{
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            width: '100%',
                        }}
                    >
                        {columns.map((column: any, columnIndex: any) => {
                            const isRight = (columnIndex + 1) % 2 === 0;
                            return (
                                <CardItem
                                    key={column.key}
                                    className={classNames(
                                        column.key,
                                        { left: !isRight },
                                        { right: isRight },
                                    )}
                                >
                                    <Label>{column.title}</Label>
                                    <Value className={column.className ? column.className : ''}>
                                        {column.render(row)}
                                    </Value>
                                </CardItem>
                            );
                        })}
                        {expandable && (
                            <CardItem
                                key={'action'}
                                className={classNames('action', 'right')}
                                onClick={() => handleExpand(index, row)}
                            >
                                {expandable.expandColumn(row, expandedCards[index])}
                            </CardItem>
                        )}
                        {expandable && expandedCards[index] && expandable.expandedRender(row, true)}
                    </Card>
                );
            })}
        </CardList>
    );
};
export { StyledCards };
